import { useEffect, useState } from 'react';
import { audio } from '@/audio/AudioService';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { useT } from '@/i18n/useI18n';
import { RARITY_COLOR } from '@/characters/characterApi';
import { useHoldSkinStore } from '@/holdskins/holdSkinStore';
import type { HoldSkinEntry } from '@/holdskins/holdSkinsApi';
import { HoldShapePreview } from '@/holdskins/HoldShapePreview';

/**
 * Owned hold-skins section embedded in the Inventory (BAG) screen. Players
 * EQUIP wall-grip shapes here; buying happens in the Shop. Unequipping returns
 * to the per-match random default pool. Only owned skins appear.
 */
export function HoldSkinsBagSection() {
  const t = useT();
  const items = useHoldSkinStore((s) => s.items);
  const equippedId = useHoldSkinStore((s) => s.equippedId);
  const loaded = useHoldSkinStore((s) => s.loaded);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  useEffect(() => { void useHoldSkinStore.getState().refresh(); }, []);

  function flash(kind: 'ok' | 'err', text: string) {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function equip(it: HoldSkinEntry) {
    setBusyId(it.id);
    try {
      await useHoldSkinStore.getState().equip(it.id);
      audio.play('ui.tap_primary');
      flash('ok', t('holds.equipped_toast', { name: it.name }));
    } catch (e: any) {
      flash('err', e?.message ?? t('holds.equip_failed'));
    }
    setBusyId(null);
  }

  async function unequip() {
    setBusyId('__unequip');
    try {
      await useHoldSkinStore.getState().unequip();
      audio.play('ui.tap_primary');
      flash('ok', t('holds.unequipped_toast'));
    } catch (e: any) {
      flash('err', e?.message ?? t('holds.equip_failed'));
    }
    setBusyId(null);
  }

  const owned = loaded ? items.filter((it) => it.owned || (it.trialUsesLeft ?? 0) > 0) : null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tag" style={{ padding: '8px 2px 8px', color: 'var(--c-purple)' }}>
        {t('holds.bag_section')}
      </div>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, fontSize: 10, color: 'var(--ink-muted)', padding: '0 2px 8px',
        }}
      >
        <span>{t('holds.random_hint')}</span>
        {equippedId && (
          <button
            disabled={busyId === '__unequip'}
            onClick={unequip}
            className="btn"
            style={{ fontSize: 10, padding: '5px 10px', flexShrink: 0 }}
          >
            {t('holds.use_random')}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!owned ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('holds.loading')}
          </div>
        ) : owned.length === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 11, padding: 12, textAlign: 'center' }}>
            {t('holds.bag_empty')}
          </div>
        ) : owned.map((it) => (
          <BagHoldCard
            key={it.id}
            item={it}
            busy={busyId === it.id}
            onEquip={() => equip(it)}
          />
        ))}
      </div>
      {toast && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            textAlign: 'center',
            background: toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function BagHoldCard({
  item, busy, onEquip,
}: {
  item: HoldSkinEntry;
  busy: boolean;
  onEquip: () => void;
}) {
  const t = useT();
  const accent = RARITY_COLOR[item.rarity];
  const preview = resolveAssetUrl(item.imageUrl);
  return (
    <div
      className="card"
      style={{
        padding: 12, display: 'flex', gap: 12, alignItems: 'center',
        borderColor: item.equipped ? 'var(--c-cyan)' : `${accent}55`,
        background: item.equipped ? 'rgba(34,211,238,0.06)' : undefined,
      }}
    >
      <HoldShapePreview url={preview} accent={accent} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="display" style={{ fontSize: 14, color: accent }}>{item.name}</span>
          <span style={{ fontSize: 8, color: accent, border: `1px solid ${accent}`, padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase' }}>
            {item.rarity}
          </span>
          {!item.owned && (item.trialUsesLeft ?? 0) > 0 && (
            <span style={{ fontSize: 8, color: 'var(--c-cyan)', border: '1px solid var(--c-cyan)', padding: '1px 5px', borderRadius: 4 }}>
              📺 {t('inv.trial_left', { n: item.trialUsesLeft ?? 0 })}
            </span>
          )}
        </div>
        {item.description && (
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 3 }}>{item.description}</div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {item.equipped ? (
            <span className="display" style={{ fontSize: 11, color: 'var(--c-cyan)', alignSelf: 'center' }}>
              {t('holds.equipped')}
            </span>
          ) : (
            <button
              disabled={busy}
              onClick={onEquip}
              className="btn btn-primary"
              style={{ fontSize: 11, padding: '7px 14px' }}
            >
              {t('holds.equip')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
